/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

import { css, html, render } from "lit";
import { customElement, property } from "lit/decorators.js";

import "@material/mwc-textfield/mwc-textfield";
import "@material/mwc-button/mwc-button";
import "@material/mwc-linear-progress/mwc-linear-progress";

import {
  ReviewRequest,
} from "../data-classes/review-request";

import { Revision } from "../revision";
import { DocumentInput } from "../document-input";
import { convertEntitiesToEntityLabels, EntityLabel } from "../entity-label";
import { convertEntitiesToGroupingyLabels } from "../grouping-label";
import { convertEntitiesToClassificationLabels } from "../classification-label";
import { Dialog } from "@material/mwc-dialog";
import { HitlElement } from "../hitl-element";

/**
 * @enum
 * Enum for navigation event
 */
enum NavEvent {
  BACK,
  PREVIOUS,
  NEXT,
}

/**
 * @class HitlReviewDocument
 */
@customElement("hitl-review-document")
export class HitlReviewDocument extends HitlElement {

  @property({ type: Number })
  reviewRequestsIndex: number = 0;

  /**
  * Close button
  */
  closeButton = document.createElement('mwc-button');

  rejectReason: string = "";
  humanRevisionStateMessage: string = "";
  humanRevisionState: string = "requested";

  /** 
   * Create an observer for monitoring splitter to setup drag sources
   */
  observer = new MutationObserver(this.setupDragSources);

  /**
   * Drag Source Element
   */
  dragSrcEl:HTMLElement | null = null;

  /**
   * Drag Counter
   */
  dragCounter = 0;

  /**
   * Source page index
   */
  srcPageIndex: any;

  /**
   * Destination page index
   */
  dstPageIndex: any;
  static override styles = css`
    .over:hover {
      background-color: blue;
    }

    div.side-panel-wrapper.ng-star-inserted {
      width: 270px;
    }
  `;

  override createRenderRoot() {
    return this;
  }

  override firstUpdated() {
    // start reviewing first document
    this.reviewDocument(this.reviewRequests[this.reviewRequestsIndex]);
    console.log(this.observer);
  }

  override render() {
    return html` <div class="content-div"></div> `;
  }

  /**
   * Get access token
   * @param refresh 
   * @returns 
   */
  override async getAccessToken(refresh = false) {
    const accessToken = await new Promise<string>((resolve, reject) => {
      const getAccessToken = new CustomEvent("get-access-token", {
        bubbles: true,
        detail: { refresh: refresh, resolve: resolve, reject: reject },
      });
      this.dispatchEvent(getAccessToken);
    });
    return accessToken;
  }

  /**
   * Review document
   * @param reviewRequest 
   */
  async reviewDocument(reviewRequest: ReviewRequest) {
    this.rejectReason = "";
    const accessToken = await this.getAccessToken(true);
    const json = await reviewRequest.getContent(accessToken);
    // initialize human review state
    this.humanRevisionState = "requested";
    this.humanRevisionStateMessage = "";
    if (reviewRequest.labels.length === 0) {
      if (reviewRequest.type === "DocSplitter") {
        reviewRequest.labels = convertEntitiesToGroupingyLabels(json.entities);
      } else if (reviewRequest.type === "DAI_COMPLEX_CLASSIFICATION") {
        reviewRequest.labels = convertEntitiesToClassificationLabels(
          json.entities
        );
      } else {
        reviewRequest.labels = convertEntitiesToEntityLabels(json.entities);
      }
    }
    const headerTitle = reviewRequest.originalFilename;
    // add accessToken to pageImageNames
    const pageImageNames = reviewRequest.pageImageNames.map((pageImageName) => {
        const url = new URL("https://" + reviewRequest.resultBucket + ".storage.googleapis.com/" + reviewRequest.pageImageUrlPrefix + "/" + pageImageName);
        url.searchParams.set("access_token", accessToken);
        return url.toString();
      });
    /*} else {
      pageImageNames = reviewRequest.pageImageNames;
    }*/
    
    /**
     * Convert Document AI document Protbuf format to Document Input format
     */
    const documentInput = new DocumentInput(
      reviewRequest.labels,
      headerTitle,
      this.reviewRequestsIndex === 0,
      this.reviewRequests.length - 1 === this.reviewRequestsIndex,
      reviewRequest.type
    );
    // create component client SDK instance
    // @ts-ignore
    const sdk = new AIUIComponentClient(process.env.API_KEY);
    // setup document view component and callback events
    var params = {
      component: "DocumentView",
      document: documentInput,
      
      /**
       * Save event triggered immediately when primary button is clicked
       */
      save: async () => {
        const reviewRequest = this.reviewRequests[this.reviewRequestsIndex];
          // ensure that the access token gets refreshed if necessary
          await this.getAccessToken(true);
        
      },
      /**
       * Save event triggered after save. 
       * It saves data in json
       * @param reviewedDocumentJson 
       * @returns 
       */
      jsonSave: async (reviewedDocumentJson: any) => {
        const reviewRequest = this.reviewRequests[this.reviewRequestsIndex];
        console.log(reviewRequest);
        const dialog = document.getElementById("dialog") as Dialog;
        const missingRequiredLabels = [];
        for (const label of reviewRequest.labels) {
          // check if required labels have been provided
          if (
            label instanceof EntityLabel &&
            label.requireCategory === "required"
          ) {
            let found = false;
            for (const entity of reviewedDocumentJson.entities) {
              if (entity.type === label.label) {
                found = true;
                if (!entity.mention_text) {
                  missingRequiredLabels.push(label.label);
                }
              }
            }
            //If required labels are not found then push the labels in missingRequiredLabels
            if (!found) {
              missingRequiredLabels.push(label.label);
            }
          }
        }
        
        const missingEntityReviews = [];
        for (const entity of reviewedDocumentJson.entities) {
          console.log(entity);
          // check if entities marked for review have been reviewed
          if (entity.provenance?.type === 4) {
            missingEntityReviews.push(entity.type);
          }
        }
        // display message if validation failed and cancel saving
        if (
          missingRequiredLabels.length > 0 ||
          missingEntityReviews.length > 0
        ) {
          dialog.heading = "Dokument Review Validierung fehlgeschlagen.";
          render(
            html`The following required entities are missing values:
              ${missingRequiredLabels.join(", ")}<br />
              The following required entities are missing a mandatory review:
              ${missingEntityReviews.join(", ")}
              <mwc-button slot="primaryAction" dialogAction="ok"
                >Ok</mwc-button
              >`,
            dialog
          );
          dialog.open = true;
          return;
        }
        if(this.dragSrcEl) {
          console.log("save due to drag");
          console.log("original JSONreviewedDocumentJson", reviewedDocumentJson);
          reviewedDocumentJson = this.movePage(reviewRequest, reviewedDocumentJson)
          console.log("page moved JSONreviewedDocumentJson", reviewedDocumentJson);
          this.reviewDocument(reviewRequest);
          return;
        }
        /**
         * Create new revision of document to track changes
         */
        const revision = new Revision(
          reviewedDocumentJson,
          this.humanRevisionState,
          this.humanRevisionStateMessage,
          this.userID
        );

        // remove lastRevisionId which is not allowed in the output
        delete revision.lastRevisionId;
        //Push new revision to reviewedDocumentJson
        reviewedDocumentJson.revisions.push(revision);
        /**
         * Content of reviewedDocumentJson as string
         */
        const content = JSON.stringify(reviewedDocumentJson);
        if (dialog) {
          dialog.heading = "Dokument wird gespeichert...";
          dialog.addEventListener("closed",() => {
            dialog.heading = ""
          });
          render(
            html`<mwc-linear-progress indeterminate></mwc-linear-progress>`,
            dialog
          );
          dialog.open = true;
        }
        reviewRequest.reviewedByUserId = this.userID;
        reviewRequest
          .saveContent(
            accessToken,
            content,
            this.rejectReason
          )
          .then(() => {
            dialog.open = false;
            this.reviewRequests = this.reviewRequests.filter(
              (_, i) => i !== this.reviewRequestsIndex
            );
            if (this.reviewRequests.length > 0) {
              this.reviewRequestsIndex = Math.min(
                this.reviewRequestsIndex,
                this.reviewRequests.length - 1
              );
              this.reviewDocument(
                this.reviewRequests[this.reviewRequestsIndex]
              );
            } else {
              this.closeReview();
            }
          })
          .catch(() => {
            dialog.innerHTML="";
            dialog.heading="";
            dialog.heading = "Der Speicherungsvorgang wurde abgebrochen.";
            this.closeButton.setAttribute("slot", "primaryAction");
            this.closeButton.setAttribute("dialogAction", "ok");
            this.closeButton.innerText = "Schließen";
            this.closeButton.classList.add("raised");
            
            dialog.appendChild(this.closeButton);
            dialog.open = true;
            dialog.addEventListener("closed",() => {
                dialog.heading = ""
                dialog.innerHTML="";
            });
          });
      },
      secondaryButtonClick: async () => {
        // ensure that the access token gets refreshed if necessary
        await this.getAccessToken(true);
        // retrieve rejection reason from user
        const dialog = document.getElementById("dialog") as Dialog;
        if (dialog) {
            dialog.innerHTML="";
          dialog.heading = "Geben Sie bitte an aus welchem Grund Sie das Dokument ablehnen";
          render(
            html` <mwc-textfield
                outlined
                style="width: 100%;margin-top: 12px;"
                required
                value=${this.rejectReason}
                label="Grund der Ablehnung"
                @change=${this.updateRejectReason}
              ></mwc-textfield>
              <mwc-button
                id="dialogSubmitButton"
                label="Dokument ablehnen"
                slot="primaryAction"
                dialogAction="ok"
                @click=${this.rejectDocument}
              ></mwc-button>`,
            dialog
          );
          dialog.open = true;
        }
      },
      navigate: async (event: NavEvent) => {
        // ensure that the access token gets refreshed if necessary
        await this.getAccessToken(true);
        if (event === NavEvent.BACK) {
          // back button clicked
          this.closeReview();
        } else {
          if (event === NavEvent.PREVIOUS) {
            // previous button clicked
            this.reviewRequestsIndex = Math.max(
              this.reviewRequestsIndex - 1,
              0
            );
          } else if (event === NavEvent.NEXT) {
            // next button clicked
            this.reviewRequestsIndex = Math.min(
              this.reviewRequestsIndex + 1,
              this.reviewRequests.length - 1
            );
          }
          this.reviewDocument(this.reviewRequests[this.reviewRequestsIndex]);
        }
      },
      escalate: async () => {
        // ensure that the access token gets refreshed if necessary
        await this.getAccessToken(true);
        console.log("Escalation event");
        const dialog = document.getElementById("dialog") as Dialog;
        if (dialog) {
          dialog.heading = "Escalate button clicked.";
          render(
            html`<mwc-button slot="primaryAction" dialogAction="ok"
              >Ok</mwc-button
            >`,
            dialog
          );
          dialog.open = true;
        }
      },
    };
  /**
   * Retrieve element for rendering the main document view UI component
   */
    const aiDocumentView = this.renderRoot.querySelector(".content-div");
    let documentViewElement = await sdk.render(aiDocumentView, params);
    if (reviewRequest.type === "DocSplitter") {
      documentViewElement.isSplitter = true;
    }
    console.log("json",json)
    console.log("imageUrls",pageImageNames)
    documentViewElement.docProtoJson = { json: json, imageUrls: pageImageNames };
    this.setupMovingPages();
  }

  updateRejectReason = async (e: Event) => {
    // ensure that the access token gets refreshed if necessary
    await this.getAccessToken(true);
    const rejectReason = (e.target as HTMLTextAreaElement).value;
    this.rejectReason = rejectReason;
  };

  rejectDocument = async () => {
    // ensure that the access token gets refreshed if necessary
    await this.getAccessToken(true);
    this.humanRevisionStateMessage = this.rejectReason;
    this.humanRevisionState = "rejected";
    (
      document.getElementsByClassName("primary-button")[0] as HTMLButtonElement
    ).click();

  };

  /**
   * Setup moving pages
   */
  setupMovingPages() {
    console.log('Setup moving pages')
    let documentSplitter = document.getElementsByTagName("ai-document-splitter")[0];

    // Setup an observer on the ai document splitter element
    const config = { childList: true };

    // Start observing the target node for configured mutations
    this.observer.observe(documentSplitter, config);

    this.setupDragSources();
  }

  /**
   * Setup drag sources
   */
  setupDragSources() {
    console.log("Setting up drag sources");
    let splitterPageContainers = Array.from(document.getElementsByClassName("ai-splitter-page-container"));

    // setup drag and drop event listeners
    splitterPageContainers.forEach(element => {
      element.setAttribute('draggable', 'true')
      element.addEventListener('dragstart', e => this.handleDragStart(e as DragEvent));
      element.addEventListener('dragend', this.handleDragEnd);
    });
  }

  /**
   * handle drag & drop for document splitting
   * @param e 
   */
  handleDragStart=(e:DragEvent) =>{
    console.log("handleDragStart", e);
    if(this.dragSrcEl != null)this.dragSrcEl.style.opacity = '0.4';
    this.setupDropTargets(e.target as HTMLElement);
    this.dragSrcEl = e.target as HTMLElement;
  }

  /**
   * Handle drag end event of document page
   * @param _ 
   */
  handleDragEnd = (_: Event) =>{
    console.log('Handle drag end')
    if(this.dragSrcEl != null)this.dragSrcEl.style.opacity = '1';
    this.dragCounter = 0;
    this.removeDropTargets();    
  }

  /**
   * Handle drag over event of document page
   * @param e 
   * @returns 
   */
  handleDragOver = (e:DragEvent) =>{
    e.preventDefault();
    console.log('Handle drag over')
    return false;
  }

  /**
   * Handle drag enter event of document page
   * @param _ 
   */
  handleDragEnter=(e:DragEvent) => {
    console.log('Drag enter', e);
    this.dragCounter++;
    if (this.dragSrcEl && this.dragSrcEl.parentElement != this.dragSrcEl && this.dragCounter == 1) {
      this.dragSrcEl.classList.add('over');
      
    };
  }

  /**
   * Handle drag leave event of document page
   * @param e 
   */
  handleDragLeave = (_:Event) =>{
    console.log('Handle drag enter')
    this.dragCounter--;
    if (this.dragCounter == 0 && this.dragSrcEl) {
      this.dragSrcEl.classList.remove('over');
    };
  }

  /**
   * Handle drop event of document page
   * @param e 
   * @returns 
   */
  handleDrop = (e:DragEvent) => {       
    e.stopPropagation();
    console.log('Handle drop is called');
    if(this.dragSrcEl && this.dragSrcEl.parentElement){
        const children = Array.from(this.dragSrcEl.parentElement.children);
        this.srcPageIndex = children.indexOf(this.dragSrcEl);
        console.log("Source page " + this.srcPageIndex);
        this.dstPageIndex = children.indexOf(this.dragSrcEl.parentElement);
        console.log("Destination page " + this.dstPageIndex);
    }

    // we need to trigger the save event to get the current document and then reload the document with the new page order
    if (this.srcPageIndex != this.dstPageIndex && this.srcPageIndex + 1 != this.dstPageIndex) {
      (document.getElementsByClassName("primary-button")[0] as HTMLElement).click();
    };

    this.dragSrcEl = null;

    return false;
  }

  /**
   * Setup drop targets
   * @param currentPageElement 
   */
  setupDropTargets(currentPageElement: any){
    console.log('Setup drop targets');
    // add toggle at the end to allow moving a page to the end
    let splitterPageContainer = currentPageElement.cloneNode(true);
    splitterPageContainer.removeChild(splitterPageContainer.lastChild!);
    while (splitterPageContainer.firstChild!.firstChild) {
      splitterPageContainer.firstChild!.removeChild(splitterPageContainer.firstChild!.firstChild);
    };
    splitterPageContainer.id = "temp-split-toggle-container";
    splitterPageContainer.style.width = null;
    currentPageElement.parentElement.appendChild(splitterPageContainer);

    let splitterPageContainers:any[] = Array.from(currentPageElement.parentElement.children);
    let currentPageIndex = splitterPageContainers.indexOf(currentPageElement);
    let excludeElements: any;
    if (currentPageIndex < splitterPageContainers.length - 1) {
      excludeElements = [currentPageElement.firstChild, splitterPageContainers[currentPageIndex + 1].firstChild];
    }
    else {
      excludeElements = [currentPageElement.firstChild];
    };

    let splitToggleContainers = Array.from(document.getElementsByClassName("ai-document-split-toggle-container"));
    splitToggleContainers.forEach(splitToggleContainer => {
      if (Array.from(splitToggleContainer.classList).includes('ai-document-split-toggle-container')) {
        if (!excludeElements.includes(splitToggleContainer)) {
          splitToggleContainer.classList.add('drop-target');
          splitToggleContainer.addEventListener('dragover', e => this.handleDragOver(e as DragEvent));
          splitToggleContainer.addEventListener('dragenter', e => this.handleDragEnter(e as DragEvent));
          splitToggleContainer.addEventListener('dragleave', this.handleDragLeave);
          splitToggleContainer.addEventListener('drop', e =>this.handleDrop(e as DragEvent));
        };
      };
    });
  }

  /**
   * Removes the drop targets
   */
  removeDropTargets(){  
    console.log('Remove drop targets');
    // remove temporary split toggle container
    let splitterPageContainer = document.getElementById("temp-split-toggle-container");
    if (splitterPageContainer) {
      console.log("SplitterPageContainer is not empty")
      splitterPageContainer.remove();
    };

    let splitToggleContainers = Array.from(document.getElementsByClassName("ai-document-split-toggle-container"))
    
    splitToggleContainers.forEach(splitToggleContainer => {
      console.log('Classes will be removed');
      splitToggleContainer.classList.remove('drop-target');
      splitToggleContainer.classList.remove('over');
      splitToggleContainer.removeEventListener('dragover', e => this.handleDragOver(e as DragEvent));
      splitToggleContainer.removeEventListener('dragenter', e => this.handleDragEnter(e as DragEvent));
      splitToggleContainer.removeEventListener('dragleave', this.handleDragLeave);
      splitToggleContainer.removeEventListener('drop', e =>this.handleDrop(e as DragEvent));
    });
  }

  /**
   * Move page
   * @param reviewRequest 
   * @param documentJson 
   * @returns 
   */
  movePage(reviewRequest: ReviewRequest, documentJson: any) {
    console.log('Move page method is executed')
    if (this.srcPageIndex < this.dstPageIndex) { // move page forward
      // first shuffle page image URLs
      reviewRequest.pageImageNames.splice(this.dstPageIndex - 1, 0, reviewRequest.pageImageNames.splice(this.srcPageIndex, 1)[0]);
      // now change page numbers
      documentJson.pages.forEach((page:any) => {
        // if page is the source page, change the page number to the destination page, note: dstPageIndex is the index of the page after which it will be inserted, thus no +1 is required for page number
        if (page.page_number - 1 === this.srcPageIndex) {
          console.log('Change page number to destination number')
          page.page_number = this.dstPageIndex;
        }
        // if page is before destination page, shift page number down
        else if (page.page_number - 1 > this.srcPageIndex && page.page_number - 1 < this.dstPageIndex) {
          page.page_number = page.page_number - 1;
          console.log('Change page number to page number before destination number')
        }
      });
      // insert page object into right position in page array
      documentJson.pages.splice(this.dstPageIndex - 1, 0, documentJson.pages.splice(this.srcPageIndex, 1)[0]);
      // switch page numbers inside entities object
      documentJson.entities.forEach((entity: any) => {
        //  array holding page_ref to delete from current entity
        let remove_page_ref_indices: Array<Number> = [];
        // array holding page_ref for new entity
        let new_entity_page_refs: Array<any> = [];
        // check if destination is in current entity
        const destinationPageInCurrentEntity = entity.page_anchor.page_refs.find((page_refs_find: any) => page_refs_find.page === this.dstPageIndex - 1);  
        console.log('Destination page is current entity', destinationPageInCurrentEntity)
        // iterate over page_refs of entity
        entity.page_anchor.page_refs.forEach((page_ref: any) => {
          // if page is the source page
          if (page_ref.page === this.srcPageIndex) {
            // shift page number down 
            page_ref.page = this.dstPageIndex - 1;    
            console.log('Shift page number down')
            // if page is not the only one in the entity and page number is not the page before the destination (note: dstPageIndex is one larger than the index of the page after which the page should be dropped)
            if (entity.page_anchor.page_refs.length > 1 && !destinationPageInCurrentEntity) {            
              new_entity_page_refs.push(page_ref);
              remove_page_ref_indices.push(entity.page_anchor.page_refs.indexOf(page_ref));             
            }
          }
          // if page is not the source page and not the destination page
          else if (page_ref.page > this.srcPageIndex && page_ref.page < this.dstPageIndex) {
            page_ref.page = page_ref.page - 1;
            // if page_ref is not the only one in the entity and page_ref is not the page before the destination (note: dstPageIndex is one larger than the index of the page after which the page should be dropped)
            if (entity.page_anchor.page_refs.length > 1 && !destinationPageInCurrentEntity) {            
              new_entity_page_refs.push(page_ref);
              remove_page_ref_indices.push(entity.page_anchor.page_refs.indexOf(page_ref));
            }
          }
        });
        // remove page_ref from entity if a new entity was created
        remove_page_ref_indices.forEach(remove_page_ref_index => {
          console.log('Remove page ref from entity if new entity was created')
          entity.page_anchor.page_refs.splice(remove_page_ref_index, 1);
        });
        // create new entity with page_ref
        if (new_entity_page_refs.length > 0) {
          console.log('create new entity with page_ref');
          const new_entity = structuredClone(entity);
          new_entity.page_anchor.page_refs = [];
          new_entity_page_refs.forEach(page_ref => {
            new_entity.page_anchor.page_refs.push(page_ref);
          });
          documentJson.entities.push(new_entity);
        };
      });
    }
    else {   // move page backwards 
      // first shuffle page image names
      reviewRequest.pageImageNames.splice(this.dstPageIndex, 0, reviewRequest.pageImageNames.splice(this.srcPageIndex, 1)[0]);
      console.log('Shuffle page image names')
      // now change page numbers
      documentJson.pages.forEach((page:any) => {
        // if page is the source page, change the page number to the destination page, note: dstPageIndex is the index of the page before which it will be inserted, thus +1 is required for page number
        if (page.page_number - 1 === this.srcPageIndex) {
          page.page_number = this.dstPageIndex + 1;
          console.log('page number is equal destination page index + 1')
        }
        // if page is before destination page, shift page number up
        else if (page.page_number - 1 < this.srcPageIndex && page.page_number - 1 >= this.dstPageIndex) {
          page.page_number = page.page_number + 1;
        }
      });
      // insert page object into right position in page array
      documentJson.pages.splice(this.dstPageIndex, 0, documentJson.pages.splice(this.srcPageIndex, 1)[0]);
      console.log("documentJson after page index change",documentJson);
      // switch page numbers inside entities object
      documentJson.entities.forEach((entity: any) => {
        //  array holding page_ref to delete from current entity
        let remove_page_ref_indices: Array<Number> = [];
        // array holding page_ref for new entity
        let new_entity_page_refs: Array<any> = [];
        // check if destination is in current entity
        const destinationPageInCurrentEntity = entity.page_anchor.page_refs.find((page_refs_find:any) => page_refs_find.page === this.dstPageIndex);  
        // iterate over page_refs of entity
        entity.page_anchor.page_refs.forEach((page_ref: any) => {        
          // if page is the source page
          if (page_ref.page === this.srcPageIndex) {
            console.log("same page")
            // shift page number up
            page_ref.page = this.dstPageIndex;
            // if page is not the only one in the entity and page number is not the destination page (note: dstPageIndex is the index of the page which the page should be dropped to)
            if (entity.page_anchor.page_refs.length > 1 && !destinationPageInCurrentEntity) {
              console.log("remove page_ref from entity", page_ref)       
              new_entity_page_refs.push(page_ref);
              remove_page_ref_indices.push(entity.page_anchor.page_refs.indexOf(page_ref));             
            }
          }
          // if page is not the source page and not the destination page
          else if (page_ref.page < this.srcPageIndex && page_ref.page >= this.dstPageIndex) {
            console.log("other page")
            page_ref.page = page_ref.page + 1;
            // if page_ref is not the only one in the entity and page_ref is not the destination page (note: dstPageIndex is the index of the page which the page should be dropped to)
            if (entity.page_anchor.page_refs.length > 1 && !destinationPageInCurrentEntity) {
              console.log("remove page_ref from entity", page_ref)
              new_entity_page_refs.push(page_ref);
              remove_page_ref_indices.push(entity.page_anchor.page_refs.indexOf(page_ref));
            }
          }
        });      
        // remove page_ref from entity if a new entity was created
        remove_page_ref_indices.forEach(remove_page_ref_index => {
          entity.page_anchor.page_refs.splice(remove_page_ref_index, 1);
        });
        // create new entity with page_ref
        if (new_entity_page_refs.length > 0) {
          const new_entity = structuredClone(entity);
          new_entity.page_anchor.page_refs = [];
          new_entity_page_refs.forEach(page_ref => {
            new_entity.page_anchor.page_refs.push(page_ref);
          });
          documentJson.entities.push(new_entity);
        };    
      });
      console.log("documentJson after entity move", documentJson)
    };
    /*if (!documentJson.pages[0].groupMetadata) {
      documentJson.pages[0].groupMetadata = {confidence:0.0, groupingLabel:groupingLabels[0]}
    }; */
    this.srcPageIndex = undefined;
    this.dstPageIndex = undefined;
    return documentJson;
  }

  closeReview() {
    console.log('Close review');
    const dialog = document.getElementById("dialog") as Dialog;
    dialog.heading ="";
    const reviewRequestUpdatedEvent = new CustomEvent(
      "manage-batch-and-queues-triggered",
      {
        bubbles: true,
        detail: this.reviewRequests,
      }
    );
    this.dispatchEvent(reviewRequestUpdatedEvent);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "hitl-review-document": HitlReviewDocument;
  }
}
